import React, {
    createContext,
    useContext,
    useState,
    useMemo,
    useEffect,
} from 'react'
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import baseTheme from '../theme'

interface ThemeContextType {
    mode: 'light' | 'dark'
    toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType>({
    mode: 'light',
    toggleTheme: () => {},
})

export const useThemeContext = () => useContext(ThemeContext)

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    // Check local storage or system preference
    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        const savedMode = localStorage.getItem('themeMode')
        if (savedMode === 'dark' || savedMode === 'light') return savedMode
        return 'light' // default to light
    })

    useEffect(() => {
        localStorage.setItem('themeMode', mode)
        // Force body background and inject CSS variables for SCSS integration
        if (mode === 'dark') {
            document.body.style.backgroundColor = '#0b0e14'
            document.documentElement.style.setProperty('--bg-page', '#0b0e14')
            document.documentElement.style.setProperty('--bg-card', '#151921')
            document.documentElement.style.setProperty(
                '--border-card',
                '#232933'
            )
            document.documentElement.style.setProperty(
                '--text-primary',
                '#f8fafc'
            )
            document.documentElement.style.setProperty(
                '--text-secondary',
                '#94a3b8'
            )
            document.documentElement.style.setProperty('--bg-hover', '#1e293b')
            document.documentElement.style.setProperty('--bg-banner', '#1a1830')
            document.documentElement.style.setProperty(
                '--border-banner',
                '#312e81'
            )
            document.documentElement.style.setProperty('--bg-sidebar', '#151921')
            document.documentElement.style.setProperty('--bg-header', '#151921')
            document.documentElement.style.setProperty('--surface-elevated', '#1e293b')
            document.documentElement.style.setProperty('--surface-recessed', '#080c12')
            document.documentElement.style.setProperty('--border-default', '#232933')
            document.documentElement.style.setProperty('--border-strong', '#3d4759')
            document.documentElement.style.setProperty('--text-tertiary', '#64748b')
            document.documentElement.style.setProperty('--primary-main', '#f2755f')
            document.documentElement.style.setProperty('--primary-alpha', 'rgba(242,117,95,0.12)')
            document.documentElement.style.setProperty('--success-alpha', 'rgba(34,197,94,0.12)')
            document.documentElement.style.setProperty('--error-alpha', 'rgba(239,68,68,0.12)')
            document.documentElement.style.setProperty('--warning-alpha', 'rgba(234,179,8,0.12)')
        } else {
            document.body.style.backgroundColor = '#f8fafc'
            document.documentElement.style.setProperty('--bg-page', '#f8fafc')
            document.documentElement.style.setProperty('--bg-card', '#ffffff')
            document.documentElement.style.setProperty(
                '--border-card',
                '#e2e8f0'
            )
            document.documentElement.style.setProperty(
                '--text-primary',
                '#0f172a'
            )
            document.documentElement.style.setProperty(
                '--text-secondary',
                '#64748b'
            )
            document.documentElement.style.setProperty('--bg-hover', '#f1f5f9')
            document.documentElement.style.setProperty('--bg-banner', '#eef2ff')
            document.documentElement.style.setProperty(
                '--border-banner',
                '#c7d2fe'
            )
            document.documentElement.style.setProperty('--bg-sidebar', '#ffffff')
            document.documentElement.style.setProperty('--bg-header', '#ffffff')
            document.documentElement.style.setProperty('--surface-elevated', '#f1f5f9')
            document.documentElement.style.setProperty('--surface-recessed', '#f8fafc')
            document.documentElement.style.setProperty('--border-default', '#e2e8f0')
            document.documentElement.style.setProperty('--border-strong', '#cbd5e1')
            document.documentElement.style.setProperty('--text-tertiary', '#94a3b8')
            document.documentElement.style.setProperty('--primary-main', '#f2755f')
            document.documentElement.style.setProperty('--primary-alpha', 'rgba(242,117,95,0.10)')
            document.documentElement.style.setProperty('--success-alpha', 'rgba(22,163,74,0.08)')
            document.documentElement.style.setProperty('--error-alpha', 'rgba(220,38,38,0.08)')
            document.documentElement.style.setProperty('--warning-alpha', 'rgba(202,138,4,0.08)')
        }
    }, [mode])

    const toggleTheme = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
    }

    const theme = useMemo(
        () =>
            responsiveFontSizes(createTheme({
                ...baseTheme,
                palette: {
                    ...baseTheme.palette,
                    mode,
                    ...(mode === 'dark'
                        ? {
                              background: {
                                  default: '#0b0e14', 
                                  paper: '#151921', 
                              },
                              text: {
                                  primary: '#f8fafc',
                                  secondary: '#94a3b8',
                              },
                              divider: '#232933',
                          }
                        : {
                              background: {
                                  default: '#f8fafc',
                                  paper: '#ffffff',
                              },
                              text: {
                                  primary: '#0f172a',
                                  secondary: '#64748b',
                              },
                          }),
                },
                components: {
                    ...baseTheme.components,
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                                border: `1px solid ${mode === 'dark' ? '#232933' : '#e2e8f0'}`,
                                boxShadow: mode === 'dark' ? '0 4px 6px -1px rgb(0 0 0 / 0.3)' : '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                            },
                        },
                    },
                    MuiTableHead: {
                        defaultProps: {
                            sx(theme) {
                                return {
                                    backgroundColor:
                                        mode === 'dark'
                                            ? '#1e293b'
                                            : theme.palette.grey[100],
                                    '.MuiTableCell-head': {
                                        borderBottom: 'none',
                                        color: theme.palette.text.secondary,
                                        backgroundColor:
                                            mode === 'dark'
                                                ? '#1e293b'
                                                : theme.palette.grey[100],
                                    },
                                }
                            },
                        },
                    },
                    MuiTableCell: {
                        defaultProps: {
                            sx(theme) {
                                return {
                                    borderBottom: `1px solid ${mode === 'dark' ? '#232933' : theme.palette.grey[200]}`,
                                }
                            },
                        },
                    },
                    MuiLinearProgress: {
                        styleOverrides: {
                            root: {
                                backgroundColor: mode === 'dark' ? '#1e293b' : '#e2e8f0',
                                borderRadius: 4,
                            },
                            bar: {
                                backgroundColor: '#f2755f',
                                borderRadius: 4,
                            },
                        },
                    },
                    // ── Global dark-mode icon / input fixes ──────────────────
                    MuiSelect: {
                        styleOverrides: {
                            icon: {
                                // Dropdown arrow visible in both modes
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                            },
                        },
                    },
                    MuiInputAdornment: {
                        styleOverrides: {
                            root: {
                                // Icons inside inputs (copy, eye, etc.)
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            notchedOutline: {
                                // Input border uses theme divider color
                                borderColor: mode === 'dark' ? '#3d4759' : 'rgba(0,0,0,0.23)',
                            },
                            input: {
                                // Input text color
                                color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                            },
                        },
                    },
                    MuiInputBase: {
                        styleOverrides: {
                            root: {
                                // Ensure text inside inputs is readable
                                color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                            },
                        },
                    },
                    MuiIconButton: {
                        styleOverrides: {
                            root: {
                                // Icon buttons visible in both modes
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                                '&:hover': {
                                    color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                                    backgroundColor: mode === 'dark' ? 'rgba(248,250,252,0.08)' : 'rgba(0,0,0,0.04)',
                                },
                                // Disabled icon buttons still legible
                                '&.Mui-disabled': {
                                    color: mode === 'dark' ? '#475569' : 'rgba(0,0,0,0.26)',
                                },
                            },
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                // Disabled contained buttons clearly visible
                                '&.Mui-disabled': {
                                    backgroundColor: mode === 'dark' ? '#1e293b' : 'rgba(0,0,0,0.12)',
                                    color: mode === 'dark' ? '#475569' : 'rgba(0,0,0,0.38)',
                                    borderColor: mode === 'dark' ? '#2d3748' : 'transparent',
                                },
                            },
                            outlined: {
                                '&.Mui-disabled': {
                                    borderColor: mode === 'dark' ? '#334155' : 'rgba(0,0,0,0.12)',
                                    color: mode === 'dark' ? '#475569' : 'rgba(0,0,0,0.38)',
                                },
                            },
                        },
                    },
                    MuiToggleButton: {
                        styleOverrides: {
                            root: {
                                // Unselected option readable in both modes
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                                '&.Mui-selected': {
                                    color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                                    fontWeight: 600,
                                },
                            },
                        },
                    },
                    MuiCheckbox: {
                        styleOverrides: {
                            root: {
                                color: mode === 'dark' ? '#64748b' : undefined,
                            },
                        },
                    },
                    MuiFormLabel: {
                        styleOverrides: {
                            root: {
                                color: mode === 'dark' ? '#94a3b8' : undefined,
                                '&.Mui-focused': {
                                    color: '#f2755f',
                                },
                            },
                        },
                    },
                    MuiMenuItem: {
                        styleOverrides: {
                            root: {
                                color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                                '&:hover': {
                                    backgroundColor: mode === 'dark' ? '#1e293b' : '#f1f5f9',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: mode === 'dark' ? '#1e3a5f' : '#e8f4fd',
                                    '&:hover': {
                                        backgroundColor: mode === 'dark' ? '#1e4070' : '#d0eafa',
                                    },
                                },
                            },
                        },
                    },
                    MuiAutocomplete: {
                        styleOverrides: {
                            paper: {
                                backgroundColor: mode === 'dark' ? '#151921' : '#ffffff',
                                border: `1px solid ${mode === 'dark' ? '#232933' : '#e2e8f0'}`,
                            },
                            option: {
                                color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                                '&[aria-selected="true"]': {
                                    backgroundColor: mode === 'dark' ? '#1e3a5f' : '#e8f4fd',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: mode === 'dark' ? '#1e293b' : '#f1f5f9',
                                },
                            },
                            clearIndicator: {
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                            },
                            popupIndicator: {
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                            },
                            endAdornment: {
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                            },
                            noOptions: {
                                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                            },
                        },
                    },
                    MuiChip: {
                        styleOverrides: {
                            root: {
                                '&.MuiChip-outlined': {
                                    borderColor: mode === 'dark' ? '#334155' : undefined,
                                    color: mode === 'dark' ? '#cbd5e1' : undefined,
                                },
                            },
                        },
                    },
                    MuiCssBaseline: {
                        styleOverrides: mode === 'dark'
                            // Native date/time picker indicator is dark by default; invert it
                            ? `::-webkit-calendar-picker-indicator { filter: invert(0.7); }`
                            : '',
                    },
                },
            })),
        [mode]
    )

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    )
}
